import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

interface State {
    blur: number;
    brightness: number;
    contrast: number;
    grayscale: number;
    hueRotate: number;
    invert: number;
    saturate: number;
    sepia: number;
    opacity: number;
    dropShadow: boolean;
    dsX: number;
    dsY: number;
    dsBlur: number;
    dsColor: string;
    imageUrl: string;
    copied: boolean;
}

const SAMPLE = '/img/1.jpg';

export default class CssFilterGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        blur: 0, brightness: 100, contrast: 100, grayscale: 0,
        hueRotate: 0, invert: 0, saturate: 100, sepia: 0, opacity: 100,
        dropShadow: false, dsX: 4, dsY: 4, dsBlur: 8, dsColor: '#000000',
        imageUrl: SAMPLE,
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get filterValue(): string {
        const { blur, brightness, contrast, grayscale, hueRotate, invert, saturate, sepia, opacity, dropShadow, dsX, dsY, dsBlur, dsColor } = this.state;
        const parts: string[] = [];
        if (blur > 0) parts.push(`blur(${blur}px)`);
        if (brightness !== 100) parts.push(`brightness(${brightness}%)`);
        if (contrast !== 100) parts.push(`contrast(${contrast}%)`);
        if (grayscale > 0) parts.push(`grayscale(${grayscale}%)`);
        if (hueRotate !== 0) parts.push(`hue-rotate(${hueRotate}deg)`);
        if (invert > 0) parts.push(`invert(${invert}%)`);
        if (saturate !== 100) parts.push(`saturate(${saturate}%)`);
        if (sepia > 0) parts.push(`sepia(${sepia}%)`);
        if (opacity !== 100) parts.push(`opacity(${opacity}%)`);
        if (dropShadow) parts.push(`drop-shadow(${dsX}px ${dsY}px ${dsBlur}px ${dsColor})`);
        return parts.join(' ') || 'none';
    }

    private get cssCode(): string {
        return `filter: ${this.filterValue};`;
    }

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    reset = () => {
        this.setState({
            blur: 0, brightness: 100, contrast: 100, grayscale: 0,
            hueRotate: 0, invert: 0, saturate: 100, sepia: 0, opacity: 100,
            dropShadow: false,
        });
    };

    render() {
        const { blur, brightness, contrast, grayscale, hueRotate, invert, saturate, sepia, opacity, dropShadow, dsX, dsY, dsBlur, dsColor, imageUrl, copied } = this.state;

        const slider = (label: string, value: number, min: number, max: number, unit: string, onChange: (v: number) => void) => (
            <div className={genStyles.field}>
                <label>{label}: <strong>{value}{unit}</strong></label>
                <input type="range" min={min} max={max} value={value} onChange={e => onChange(+e.target.value)} />
            </div>
        );

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Image</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="cf-img">Image URL or path</label>
                        <input id="cf-img" type="text" value={imageUrl}
                            onChange={e => this.setState({ imageUrl: e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}>
                        <span className={genStyles.sectionTitle}>Filters</span>
                        <button className={genStyles.iconBtn} onClick={this.reset}>↺ Reset</button>
                    </div>
                    {slider('blur', blur, 0, 20, 'px', v => this.setState({ blur: v }))}
                    {slider('brightness', brightness, 0, 200, '%', v => this.setState({ brightness: v }))}
                    {slider('contrast', contrast, 0, 200, '%', v => this.setState({ contrast: v }))}
                    {slider('grayscale', grayscale, 0, 100, '%', v => this.setState({ grayscale: v }))}
                    {slider('hue-rotate', hueRotate, 0, 360, '°', v => this.setState({ hueRotate: v }))}
                    {slider('invert', invert, 0, 100, '%', v => this.setState({ invert: v }))}
                    {slider('saturate', saturate, 0, 300, '%', v => this.setState({ saturate: v }))}
                    {slider('sepia', sepia, 0, 100, '%', v => this.setState({ sepia: v }))}
                    {slider('opacity', opacity, 0, 100, '%', v => this.setState({ opacity: v }))}
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Drop shadow</span></div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={dropShadow}
                            onChange={e => this.setState({ dropShadow: e.target.checked })} />
                        Enable drop-shadow
                    </label>
                    {dropShadow && (
                        <div className={genStyles.fieldRow}>
                            <div className={genStyles.field}>
                                <label>x</label>
                                <input type="number" inputMode="decimal" value={dsX} onChange={e => this.setState({ dsX: +e.target.value })} />
                            </div>
                            <div className={genStyles.field}>
                                <label>y</label>
                                <input type="number" inputMode="decimal" value={dsY} onChange={e => this.setState({ dsY: +e.target.value })} />
                            </div>
                            <div className={genStyles.field}>
                                <label>blur</label>
                                <input type="number" inputMode="decimal" value={dsBlur} onChange={e => this.setState({ dsBlur: +e.target.value })} />
                            </div>
                            <div className={genStyles.field}>
                                <label>color</label>
                                <input type="color" value={dsColor} style={{ height: 38, padding: 4 }}
                                    onChange={e => this.setState({ dsColor: e.target.value })} />
                            </div>
                        </div>
                    )}
                </div>
            </>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Preview"
                preview={
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={imageUrl} alt="filter preview"
                            style={{ filter: this.filterValue, maxWidth: '100%', maxHeight: 240, borderRadius: 8 }} />
                    </div>
                }
                codeLabel="CSS"
                codeMeta={`${this.cssCode.length} chars`}
                code={this.cssCode}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
